/**
 * Text the platform computed in English, shown in Arabic.
 *
 * The web engines build their reasons through t(), so the demonstration
 * speaks Arabic already. The platform's engines are the same engines in
 * Python and send the same sentences in English — "107 is taken by SV1483
 * from 19:34 to 20:19." — with the values already filled in. This matches such
 * a sentence against the English templates the dictionary already holds
 * ("{code} is taken by {who} from {from} to {to}."), takes the values back
 * out, translates any of them that are themselves sentences, and fills the
 * Arabic. Plural templates choose their Arabic form by the number found.
 *
 * Nothing new to translate: a sentence the web engine can say is a sentence
 * this can read. One it cannot match is shown as it came, never guessed at.
 */
import { AR, getLang } from "./index";

interface Template {
  re: RegExp;
  names: string[];
  ar: string;
  plural: boolean;
  literal: number;
}

let cache: Template[] | null = null;

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function templates(): Template[] {
  if (cache) return cache;
  const out: Template[] = [];
  for (const [en, ar] of Object.entries(AR)) {
    if (!en.includes("{")) continue;
    const plural = en.includes("|");
    for (const form of plural ? en.split("|") : [en]) {
      const names: string[] = [];
      const pattern = escape(form).replace(/\\\{(\w+)\\\}/g, (_m, n: string) => {
        names.push(n);
        return n === "n" ? "(-?\\d+(?:\\.\\d+)?)" : "(.*?)";
      });
      const literal = form.replace(/\{\w+\}/g, "").trim().length;
      /* a template that is almost all placeholder would match anything */
      if (literal < 3) continue;
      out.push({ re: new RegExp(`^${pattern}$`, "s"), names, ar, plural, literal });
    }
  }
  /* the most specific first: more fixed words, fewer guesses */
  out.sort((a, b) => b.literal - a.literal);
  cache = out;
  return out;
}

const fill = (s: string, vars: Record<string, string>) => s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? vars[k] : m));

function plural(ar: string, n: number): string {
  const forms = ar.split("|");
  const order = ["zero", "one", "two", "few", "many", "other"];
  const rule = n === 0 ? "zero" : new Intl.PluralRules("ar").select(n);
  return forms[order.indexOf(rule)] ?? forms[forms.length - 1];
}

const ARABIC = /[؀-ۿ]/;

/** A computed sentence in the reader's language: the Arabic where a template reads it, else as it came. */
export function tx(text: string | null | undefined): string {
  if (!text) return text ?? "";
  if (getLang() !== "ar") return text;
  return translate(text, 0);
}

function translate(text: string, depth: number): string {
  if (depth > 4 || ARABIC.test(text)) return text;
  const exact = AR[text];
  if (exact !== undefined && !exact.includes("|")) return exact;
  for (const tpl of templates()) {
    const m = tpl.re.exec(text);
    if (!m) continue;
    const vars: Record<string, string> = {};
    tpl.names.forEach((n, i) => {
      const v = m[i + 1] ?? "";
      vars[n] = n === "n" ? v : translate(v, depth + 1);
    });
    const form = tpl.plural ? plural(tpl.ar, Number(vars.n)) : tpl.ar;
    return fill(form, vars);
  }
  /* sentences the engines join: "a; b" and "x · y" */
  if (text.includes("; ")) return text.split("; ").map((p) => translate(p, depth + 1)).join("؛ ");
  if (text.includes(" · ")) return text.split(" · ").map((p) => translate(p, depth + 1)).join(" · ");
  return text;
}

/** For tests: forget the compiled templates. */
export function resetComputed(): void {
  cache = null;
}
